import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useState } from 'react';
import { useAsyncEffect, useDidUpdate } from 'rooks';
import { useIsCurrentAssetGroup } from '@/assetGroupEditor/assetGroup.store';
import { Checkbox } from '@/core/Checkbox.atom';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { UnitOfMeasure } from '@/core/UnitOfMeasure.atom';
import { sqItemTableEditorStore, sqScalingTableEditorStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { onEnterKeypress } from '@/core/onEnterKeypress.util';
import { onEventExceptDisplayDropdown } from '@/displays/displays.utilities';
import { HierarchyNodeType } from '@/search/search.constants';
import { useSearchActions } from '@/search/search.store';
import { getItemTableTypes, getScalingTableTypes } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { extractDisplayUnitFromIncludedProperties, resolveUnitOfMeasure } from '@/utilities/unit.utilities';
import { isAsset, isDatafile, isDisplay, isHierarchy, isTableDefinition, itemIcon } from '@/utilities/utilities';
import { SeeqNames } from '@seeqdev/constants';
import { faChevronRight } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { SelectedSearchItem } from '@/search/search.types';
import type { AnyProperty } from '@/utilities.types';
import type { HierarchyNodeOutputV1 } from '@seeqdev/api/model/HierarchyNodeOutputV1';

const CONDITION_TYPES = [
  SeeqNames.Types.Condition,
  SeeqNames.Types.StoredCondition,
  SeeqNames.Types.CalculatedCondition,
] as const;

export interface SearchResultProps {
  searchResultIcons: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  item: any;
  items?: any[];
  customIconClasses?: string;
  onClickItem: (item: AnyProperty) => void;
  showItemSelection?: boolean;
  isSelectingAsset?: boolean;
  searchTypes?: string[];
  itemSelected?: boolean;
  isSelected?: boolean;
  isAllUnselectable?: boolean;
  highlightItem?: boolean;
  onSelectItem?: (item: any) => void;
  assetGroupEditorVisible?: boolean;
  showOnlyConditions?: boolean;
  doNotDisableOnEdit?: boolean;
  noNavigate?: boolean;
}

export const SearchResult = ({
  searchResultIcons,
  item,
  items,
  onClickItem,
  customIconClasses,
  onSelectItem,
  isSelectingAsset = false,
  searchTypes,
  itemSelected = false,
  isSelected = false,
  isAllUnselectable = false,
  showItemSelection = false,
  highlightItem = false,
  assetGroupEditorVisible = false,
  showOnlyConditions = false,
  doNotDisableOnEdit = false,
  noNavigate = false,
}: SearchResultProps) => {
  const isEditingAssetGroup = useIsCurrentAssetGroup(item.id) && assetGroupEditorVisible;
  const isEditingTableDefinition = useFluxPath(
    sqScalingTableEditorStore,
    () => sqScalingTableEditorStore.tableId === item.id,
  );
  const isEditingItemTable = useFluxPath(sqItemTableEditorStore, () => sqItemTableEditorStore.tableId === item.id);
  const tableTypeProp = item.includedProperties?.[SeeqNames.Properties.TableType]?.value;

  const { clearSelectedItems, toggleSelectedItem } = useSearchActions();
  const isScalingTableThatCanBeNavigated =
    isTableDefinition(item) && getScalingTableTypes().includes(tableTypeProp) && !doNotDisableOnEdit;
  const isItemTableThatCanBeNavigated = isTableDefinition(item) && getItemTableTypes().includes(tableTypeProp);
  const isNavigableHierarchy = isHierarchy(item);
  const isHierarchyNode = item.type === HierarchyNodeType && !!item.node;
  const node = isHierarchyNode ? (item.node as HierarchyNodeOutputV1) : undefined;
  const type = node?.itemMetadata?.type || item.type;
  const isPartOfHierarchyAndHasChildren = isHierarchyNode && !item.node.leaf;
  const showNavigateCaret =
    isAsset(item) ||
    isDatafile(item) ||
    isScalingTableThatCanBeNavigated ||
    isItemTableThatCanBeNavigated ||
    isNavigableHierarchy ||
    isPartOfHierarchyAndHasChildren ||
    item?.hasActiveChildren ||
    (item.ancestors?.length === 1 &&
      item.ancestors?.some((ancestor: any) => ancestor.type === SeeqNames.Types.TableDefinition));
  const resolvedUom = resolveUnitOfMeasure(extractDisplayUnitFromIncludedProperties(item));

  const showIcons =
    (!isSelectingAsset && !searchTypes) ||
    (searchTypes && !searchTypes.includes(SeeqNames.Types.Asset) && searchTypes.includes(type));

  const [isItemSelected, setSelected] = useState(false);
  const [icons, setIcons] = useState<React.ReactNode>(null);

  useDidUpdate(() => setSelected(itemSelected), [itemSelected]);

  useAsyncEffect(async () => {
    const newIcons = await searchResultIcons(item);
    setIcons(newIcons);
  }, [searchResultIcons, item]);

  const unfocusSearchResult = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    (e.target as HTMLElement).blur();
  };

  const isAssetOrAssetNode = isAsset(item) || isAsset(node?.itemMetadata) || type === HierarchyNodeType;

  const renderCheckbox = () => {
    if (showItemSelection && (!isAllUnselectable || assetGroupEditorVisible)) {
      return (
        <Checkbox
          id={`select_${item.id}`}
          label=""
          layoutXstyle={[
            styles.selection,
            (isTableDefinition(item) ||
              isHierarchy(item) ||
              (!assetGroupEditorVisible && (isAssetOrAssetNode || isDisplay(item) || item?.hasActiveChildren))) &&
              styles.hiddenSelection,
          ]}
          checkboxXstyle={Boolean(icons) && styles.checkboxInput}
          isChecked={isItemSelected}
          onClick={() => {
            toggleSelectedItem(item.id, items);
            setSelected((isSelected) => !isSelected);
          }}
          flexAlign="center"
        />
      );
    }

    return <></>;
  };

  const searchResult = (
    <div
      data-testid={icons ? 'searchResultWithIcons' : 'searchResult'}
      aria-label={item.name}
      tabIndex={0}
      onMouseLeave={unfocusSearchResult}
      onClick={() => {
        const isOnlySelectingAssets = searchTypes?.includes(SeeqNames.Types.Asset) && searchTypes.length === 1;
        if (
          (!item.hasActiveChildren && !isOnlySelectingAssets) ||
          (isOnlySelectingAssets && !item.hasActiveChildren && isAsset(item)) ||
          doNotDisableOnEdit
        ) {
          onSelectItem?.(item);
        }
      }}
      {...stylex.props(
        styles.row,
        (isEditingTableDefinition || isEditingItemTable || isSelected) && styles.selected,
        (isEditingTableDefinition || isEditingItemTable) && styles.editingTable,
        isEditingAssetGroup && !doNotDisableOnEdit && styles.disabled,
        isEditingAssetGroup && !doNotDisableOnEdit && styles.disabledAppearance,
      )}
      // Preserve the shared theme animation until its product-specific keyframes migrate.
      className={clsx(
        stylex.props(
          styles.row,
          (isEditingTableDefinition || isEditingItemTable || isSelected) && styles.selected,
          (isEditingTableDefinition || isEditingItemTable) && styles.editingTable,
          isEditingAssetGroup && !doNotDisableOnEdit && styles.disabled,
          isEditingAssetGroup && !doNotDisableOnEdit && styles.disabledAppearance,
        ).className,
        highlightItem && 'highlightFade',
      )}>
      {renderCheckbox()}

      <div
        data-testid="searchResultItem"
        {...stylex.props(styles.item)}
        onKeyUp={onEnterKeypress(() => !isEditingAssetGroup && onClickItem(item))}
        onClick={onEventExceptDisplayDropdown(() => {
          if (!isEditingAssetGroup) {
            onClickItem(item);
            clearSelectedItems();
          }
        })}>
        <div {...stylex.props(styles.actions)}>
          {isSelectingAsset && icons}
          <Icon
            icon={itemIcon(item)}
            testId="searchItemIcon"
            type="inherit"
            extraClassNames={customIconClasses}
            xstyle={[styles.itemIcon, !customIconClasses && styles.mutedIcon]}
            large={true}
          />
        </div>

        <div {...stylex.props(styles.description)}>
          <div data-testid="searchResultName">
            <span>{item.name} </span>
            <UnitOfMeasure unitOfMeasure={resolvedUom} />
          </div>
          {item.description && (
            <div {...stylex.props(styles.descriptionText)} data-testid="searchResultDescription">
              {item.description}
            </div>
          )}
          {(item.assets?.[0]?.formattedName || item?.datasource?.name) && (
            <div {...stylex.props(styles.source)}>{item.assets?.[0]?.formattedName || item?.datasource?.name}</div>
          )}
        </div>

        <div {...stylex.props(styles.navigation)}>
          <div data-testid="selectOrNavigate" {...stylex.props(styles.actions)}>
            {showIcons && icons}

            {showNavigateCaret && !noNavigate && (
              <Icon
                icon={faChevronRight}
                type="inherit"
                extraClassNames={customIconClasses}
                xstyle={[styles.navigateIcon, !customIconClasses && styles.interactiveIcon]}
                testId="arrowIcon"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const getSearchResult = () => {
    return showOnlyConditions && !(CONDITION_TYPES.includes(type) || item.hasActiveChildren) ? undefined : searchResult;
  };

  return (
    <HoverTooltip text={isEditingAssetGroup ? 'SEARCH_DATA.NO_ACTIONS_WHILE_EDITING' : ''}>
      {getSearchResult()}
    </HoverTooltip>
  );
};

export const styles = stylex.create({
  row: {
    display: 'flex',
    flexDirection: 'row',
    flex: '0 0 auto',
    cursor: 'pointer',
    color: colors.bodyText,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: colors.dataPanelBorder,
    outline: { 'default': null, ':hover': 'none', ':focus': 'none' },
    backgroundColor: { 'default': null, ':hover': colors.legacyToolsRowHover, ':focus': colors.legacyToolsRowFocus },
  },
  selected: { backgroundColor: colors.legacyToolsRowHover, borderBottomColor: colors.dataPanelSelectionBorder },
  editingTable: { borderBottomColor: colors.dataPanelBorder },
  disabledAppearance: { opacity: 0.44 },
  disabled: {
    cursor: 'not-allowed',
    fontStyle: 'italic',
    backgroundColor: { 'default': null, ':hover': 'transparent', ':focus': 'transparent' },
    borderBottomColor: {
      'default': colors.dataPanelBorder,
      ':hover': colors.legacyToolsGroupBorder,
      ':focus': colors.legacyToolsGroupBorder,
    },
  },
  selection: { marginLeft: '0.3125rem', marginRight: '0.3125rem' },
  hiddenSelection: { visibility: 'hidden' },
  checkboxInput: { margin: 0 },
  item: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    overflow: 'hidden',
    justifyContent: 'space-between',
    paddingTop: '0.3125rem',
    paddingBottom: '0.3125rem',
  },
  actions: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  itemIcon: { paddingLeft: '0.3125rem', paddingRight: '0.625rem', lineHeight: 'normal', verticalAlign: 'middle' },
  mutedIcon: { color: colors.mutedForeground },
  description: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
    justifyContent: 'center',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  descriptionText: { fontSize: '75%', lineHeight: '90%', paddingBottom: '0.3125rem' },
  source: {
    fontSize: '75%',
    lineHeight: '90%',
    paddingBottom: '0.1875rem',
    color: colors.mutedForeground,
    fontStyle: 'italic',
  },
  navigation: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  navigateIcon: { marginRight: '0.3125rem' },
  interactiveIcon: {
    color: {
      'default': colors.legacyToolsLink,
      ':hover': colors.legacyToolsPrimary,
      ':focus': colors.legacyToolsPrimary,
    },
  },
});
